import React, { useMemo, useState } from "react";
import { Form, Spin } from "antd";
import { observer } from "mobx-react-lite";
import TableSearchState from "@/scenes/OrderManagement/redux/states/TableSearchState";
import { useLinkedProductSearchFieldsStore } from "./LinkedProductSearchFieldsStore";
import AutoDismissToolTip from "@/components/AutoDismissToolTip";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { difference, uniq } from "lodash";
import Highlight from "@/components/Highlight";
import { HotProduct, HotProductSku } from "./type";
import {
    FlexibleSelectData,
    SelectType,
} from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/FlexibleSelect/type";
import FlexibleSelect from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/FlexibleSelect";
import useInitialDataSourceValue from "./useInitialDataSourceValue";
import { useUpdateEffect } from "ahooks";

interface OrderItemExternalIdFieldProps {
    shopId?: number;
}

export const productPrefix = "__INTERNAL__PRODUCT__FHY__";
export const skuPrefix = "__INTERNAL__SKU__FHY__";

export enum ExternalIdType {
    Product = "Product",
    SKU = "SKU",
}

/** 构建前端内部Product和SKU externalId */
export function buildInternalExternalId(externalId: string, type: ExternalIdType) {
    if (!externalId) return externalId;

    if (type === ExternalIdType.Product) {
        return productPrefix + externalId;
    }

    return skuPrefix + externalId;
}

export function getRealExternalId(externalId: string): [string, ExternalIdType] {
    if (!externalId) return [externalId, undefined];

    if (externalId.startsWith(productPrefix)) {
        const productExternalId = externalId.split(productPrefix)?.[1];

        if (productExternalId) {
            return [productExternalId, ExternalIdType.Product];
        }
    } else if (externalId.startsWith(skuPrefix)) {
        const skuExternalId = externalId.split(skuPrefix)?.[1];

        if (skuExternalId) {
            return [skuExternalId, ExternalIdType.SKU];
        }
    } else {
        return [externalId, undefined];
    }

    return [externalId, undefined];
}

interface OrderItemExternalIdFieldCoreProps
    extends OrderItemExternalIdFieldProps,
        Pick<TableSearchState, "orderItemTitleValue" | "orderItemExternalIdValue"> {}

const ORDER_ITEM_EXTERNAL_FIELD_MULTIPLE = "ORDER_ITEM_EXTERNAL_FIELD_MULTIPLE";

const OrderItemExternalIdFieldCore: React.FC<OrderItemExternalIdFieldCoreProps> = observer((props) => {
    const { orderItemTitleValue = {}, orderItemExternalIdValue = {}, shopId } = props;
    const orderManagementStore = useOrderManagementStore();
    const { onFocusLoad, sameProductExternalIdMap, sameSkuExternalIdMap } = useLinkedProductSearchFieldsStore();
    const [searchText, updateSearchText] = useState("");

    const filteredSameSkus = useMemo(() => {
        const productSkus: HotProductSku[] = [];

        for (const [, group] of sameSkuExternalIdMap) {
            /** group中的所有sku商品编码相同，只取第一个展示出来 */
            if (group[0]) {
                productSkus.push(group[0]);
            }
        }

        return productSkus;
    }, [sameSkuExternalIdMap]);

    const filteredSameProducts = useMemo(() => {
        const products: HotProduct[] = [];

        /** group中的所有product商品编码相同，只取第一个展示出来 */
        for (const [, group] of sameProductExternalIdMap) {
            if (group[0]) {
                products.push(group[0]);
            }
        }

        return products;
    }, [sameProductExternalIdMap]);

    useUpdateEffect(() => {
        if (!orderManagementStore?.table?.form) return;

        const isSelectAll =
            orderItemExternalIdValue.prefixedExternalIds?.length === initialDataSourceValue.length &&
            orderItemExternalIdValue.prefixedExternalIds?.length > 0 &&
            initialDataSourceValue?.length > 0;

        const isIndeterminate = !isSelectAll && orderItemExternalIdValue.prefixedExternalIds?.length > 0;

        orderManagementStore.table.form.setFieldsValue({
            orderItemExternalIdValue: {
                isSelectAll,
                isIndeterminate,
            },
        });
    }, [orderItemExternalIdValue.prefixedExternalIds]);

    function handleClear() {
        orderManagementStore.table.form.setFieldsValue({
            orderItemExternalIdValue: {
                orderItemExternalId: "",
                prefixedExternalId: undefined,
                prefixedExternalIds: [],
                externalIdFieldSkuKeys: undefined,
                isSelectAll: false,
                isIndeterminate: false,
            },
        });
    }

    /** 选中了商品名称,仅在该商品名称下的上下文中操作 */
    let productKeys = [];
    if (orderItemTitleValue.productKeys?.length) {
        productKeys = orderItemTitleValue.productKeys;
    } else if (orderItemTitleValue.productKey) {
        productKeys = [orderItemTitleValue.productKey];
    }

    /**
     * 获取某个商品编码对应的skuKeys
     */
    function getExternalIdFieldSkuKeysByExternalId(prefixedExternalId: string) {
        let skuInfoKeys = [];

        const [realExternalId, type] = getRealExternalId(prefixedExternalId);

        if (!type) return;

        if (type === ExternalIdType.Product) {
            let productList = sameProductExternalIdMap.get(realExternalId);

            productList.forEach((it) => {
                if (productKeys?.length && !productKeys.includes(it.productKey)) return;

                it.skus.forEach(({ skuKey }) => {
                    skuInfoKeys.push(skuKey);
                });
            });
        } else {
            const skuList = sameSkuExternalIdMap.get(realExternalId);

            skuList.forEach((it) => {
                if (productKeys?.length && !productKeys.includes(it.productKey)) return;

                skuInfoKeys.push(it.skuKey);
            });
        }

        skuInfoKeys = uniq(skuInfoKeys);

        return skuInfoKeys;
    }

    function handleChange(orderItemExternalId: string, prefixedExternalId: string, prefixedExternalIds: string[]) {
        if (prefixedExternalIds) {
            let skuKeys = [];

            prefixedExternalIds.forEach((_privateExternalId) => {
                skuKeys.push(...getExternalIdFieldSkuKeysByExternalId(_privateExternalId));
            });

            skuKeys = uniq(skuKeys);

            orderManagementStore.table.form.setFieldsValue({
                orderItemExternalIdValue: {
                    externalIdFieldSkuKeys: skuKeys,
                    orderItemExternalId: "",
                    prefixedExternalIds: prefixedExternalIds,
                    prefixedExternalId: undefined,
                },
            });
        } else if (prefixedExternalId) {
            const skuKeys = getExternalIdFieldSkuKeysByExternalId(prefixedExternalId);

            orderManagementStore.table.form.setFieldsValue({
                orderItemExternalIdValue: {
                    orderItemExternalId: "",
                    prefixedExternalId,
                    prefixedExternalIds: [],
                    externalIdFieldSkuKeys: skuKeys,
                    isIndeterminate: false,
                    isSelectAll: false,
                },
            });
        } else {
            orderManagementStore.table.form.setFieldsValue({
                orderItemExternalIdValue: {
                    prefixedExternalId: undefined,
                    prefixedExternalIds: [],
                    orderItemExternalId,
                    externalIdFieldSkuKeys: undefined,
                    isSelectAll: false,
                    isIndeterminate: false,
                },
            });
        }
    }

    const inputTextMap = useMemo<Map<string, string>>(() => {
        const map = new Map<string, string>();

        filteredSameProducts?.forEach((it) => {
            const key = buildInternalExternalId(it.productExternalId, ExternalIdType.Product);

            map.set(key, it.productExternalId);
        });

        filteredSameSkus?.forEach((it) => {
            const key = buildInternalExternalId(it.skuExternalId, ExternalIdType.SKU);

            map.set(key, it.skuExternalId);
        });

        return map;
    }, [filteredSameProducts, filteredSameSkus]);

    function handleFocusLoad() {
        updateSearchText("");

        onFocusLoad();
    }

    function handleMultipleChange(multiple: boolean) {
        updateSearchText("");
        handleClear();

        if (multiple) {
            localStorage.setItem(ORDER_ITEM_EXTERNAL_FIELD_MULTIPLE, "1");
        } else {
            localStorage.removeItem(ORDER_ITEM_EXTERNAL_FIELD_MULTIPLE);
        }
    }

    function handleSelectAllChange(checked: boolean) {
        let nextExternalIdFieldSkuKeys: string[] = [...(orderItemExternalIdValue.externalIdFieldSkuKeys || [])];
        let nextPrefixedExternalIds: string[] = [...(orderItemExternalIdValue.prefixedExternalIds || [])];

        handleClear();

        if (checked) {
            for (let i = 0; i < dataSource.length; i++) {
                const value = dataSource[i].value;
                const skuKeys = getExternalIdFieldSkuKeysByExternalId(value);

                nextExternalIdFieldSkuKeys.push(...skuKeys);
                nextPrefixedExternalIds.push(value);
            }

            nextExternalIdFieldSkuKeys = uniq(nextExternalIdFieldSkuKeys);
            nextPrefixedExternalIds = uniq(nextPrefixedExternalIds);
        } else {
            if (searchText) {
                const unSelectedExternalIdFieldSkuKeys: string[] = [];
                const unSelectedPrefixedExternalIds: string[] = [];

                for (let i = 0; i < dataSource.length; i++) {
                    const value = dataSource[i].value;
                    const skuKeys = getExternalIdFieldSkuKeysByExternalId(value);

                    unSelectedExternalIdFieldSkuKeys.push(...skuKeys);
                    unSelectedPrefixedExternalIds.push(value);
                }

                nextPrefixedExternalIds = difference(nextPrefixedExternalIds, unSelectedPrefixedExternalIds);
                nextExternalIdFieldSkuKeys = difference(nextExternalIdFieldSkuKeys, unSelectedExternalIdFieldSkuKeys);
            } else {
                nextExternalIdFieldSkuKeys = [];
                nextPrefixedExternalIds = [];
            }
        }

        orderManagementStore.table.form.setFieldsValue({
            orderItemExternalIdValue: {
                orderItemExternalId: "",
                prefixedExternalId: undefined,
                prefixedExternalIds: nextPrefixedExternalIds,
                externalIdFieldSkuKeys: nextExternalIdFieldSkuKeys,
            },
        });
    }

    /** 防止唯一键冲突 */
    const externalIdSet = new Set<string>();
    const dataSource: FlexibleSelectData[] = [];

    /** 商品级别 */
    filteredSameProducts?.forEach((hotProduct) => {
        const prefixedProductExternalId = buildInternalExternalId(hotProduct.productExternalId, ExternalIdType.Product);

        if (externalIdSet.has(prefixedProductExternalId)) return null;
        if (!hotProduct.productExternalId) return null;

        /** 由于相同的product编码会被合并在一起，需要找到这个相同product编码的群组，只要该群组其中一个命中商品名称的上下文，都要将该数据源展示出来 */
        const sameExternalIdProducts = sameProductExternalIdMap.get(hotProduct.productExternalId);
        if (
            productKeys.length &&
            !productKeys.some((key) => sameExternalIdProducts?.some((it) => it.productKey === key))
        ) {
            return null;
        }

        if (!hotProduct.productExternalId.includes(searchText)) {
            /** 处理输入了商品编码的情况 */
            return null;
        }

        externalIdSet.add(prefixedProductExternalId);

        const label = hotProduct.productExternalId;

        dataSource.push({
            label: <Highlight text={label} highlightTexts={[searchText]} />,
            value: prefixedProductExternalId,
            inputText: label,
        });
    });

    /** SKU级别 */
    filteredSameSkus?.forEach((hotProductSku) => {
        const prefixedSkuExternalId = buildInternalExternalId(hotProductSku.skuExternalId, ExternalIdType.SKU);

        if (externalIdSet.has(prefixedSkuExternalId)) return null;
        if (!hotProductSku.skuExternalId) return null;

        /** 由于相同的sku编码会被合并在一起，需要找到这个相同sku编码的群组，只要该群组其中一个命中商品名称的上下文，都要将该数据源展示出来 */
        const sameExternalIdProductSkus = sameSkuExternalIdMap.get(hotProductSku.skuExternalId);
        if (
            productKeys.length &&
            !productKeys.some((key) => sameExternalIdProductSkus?.some((it) => it.productKey === key))
        ) {
            return null;
        }

        if (!hotProductSku.skuExternalId.includes(searchText)) {
            /** 处理输入了商品编码的情况 */
            return null;
        }

        externalIdSet.add(prefixedSkuExternalId);

        const label = hotProductSku.skuExternalId;

        inputTextMap.set(prefixedSkuExternalId, label);

        dataSource.push({
            label: <Highlight text={label} highlightTexts={[searchText]} />,
            value: prefixedSkuExternalId,
            inputText: label,
        });
    });

    const initialDataSourceValue = useInitialDataSourceValue(dataSource, searchText);

    const defaultMultiple = !!localStorage.getItem(ORDER_ITEM_EXTERNAL_FIELD_MULTIPLE);

    return (
        <AutoDismissToolTip title="模糊搜索请加上*号，如SPBM*或*SPBM" trigger="click">
            <Form.Item name="orderItemExternalIdValue" label={<span className="search-field-label">商品编码</span>}>
                <FlexibleSelect
                    defaultMultiple={defaultMultiple}
                    type={SelectType.ORDER_ITEM_EXTERNAL_ID}
                    data={dataSource}
                    inputTextMap={inputTextMap}
                    onFocus={handleFocusLoad}
                    onMultipleChange={handleMultipleChange}
                    onChange={handleChange}
                    onSearch={updateSearchText}
                    inputValue={orderItemExternalIdValue.orderItemExternalId}
                    singleValue={orderItemExternalIdValue.prefixedExternalId}
                    multipleValue={orderItemExternalIdValue.prefixedExternalIds}
                    onClear={handleClear}
                    shopId={shopId}
                    searchText={searchText}
                    onSelectAllChange={handleSelectAllChange}
                    isIndeterminate={orderItemExternalIdValue?.isIndeterminate}
                    isSelectAll={orderItemExternalIdValue?.isSelectAll}
                />
            </Form.Item>
        </AutoDismissToolTip>
    );
});

/** 需要将商品（product）的编码和sku的编码都展示出来 */
const OrderItemExternalIdField: React.FC<OrderItemExternalIdFieldProps> = (props) => {
    const { loading } = useLinkedProductSearchFieldsStore();
    const orderItemTitleValue = Form.useWatch("orderItemTitleValue");
    const orderItemExternalIdValue = Form.useWatch("orderItemExternalIdValue");

    return (
        <Spin spinning={loading}>
            <OrderItemExternalIdFieldCore
                {...props}
                orderItemExternalIdValue={orderItemExternalIdValue}
                orderItemTitleValue={orderItemTitleValue}
            />
        </Spin>
    );
};

export default observer(OrderItemExternalIdField);
